import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container, Grid } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { useStyles } from "./style";
import EzControlledCalendarWithFloatingLabel from "../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import { getFormBaseValidationRule } from "../../../utils/form/form-utils";
import EzControlledDropDowntWithFloatingLabel from "../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";

import EzControlledMultiSelecttWithFloatingLabel from "../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";

import {
  bookingTypeDD,
  listStatusDD,
} from "../ReservationsDetailReport/CreateReservationsDetailReport/constant";
import { ReservationsReservationsBreadcrumb } from "../ReservationsReservations/CreateReservationsReservations/constant";
import { printTableData } from "../../../utils/printUtils";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;
const ReservationsDetailReport = (props) => {
  const classes = useStyles();
  const {
    fetchReservationsDetailReports,
    tableData,
    setDeleteConfirmationPopupVisibility,
    exportReservationsDetailReport,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    handleCreateReservationsDetailReport,
    editReservationsDetailReportRoute,
    viewReservationRoute,
    RateNetworkDD,
    locationDD,
    setIsSearch,
    detailReportFilters,
    setDetailReportFilters,
    clearDetailReportFilters,
    tableColumnFilters,
    setTableColumnFilters,
    clearTableColumnFilters,
    savedTableState,
    onTableStateChange,
  } = props;
  const {
    control,
    formState: { errors, isDirty },
    watch,
    register,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: detailReportFilters,
  });

  useEffect(() => {
    reset(detailReportFilters);
  }, [detailReportFilters, reset]);

  // Initialize hasSearched based on whether we already have filters (e.g., returning from detail view)
  const [hasSearched, setHasSearched] = useState(() => {
    return detailReportFilters && 
      (detailReportFilters.startDate || detailReportFilters.endDate || 
       detailReportFilters.location || detailReportFilters.network);
  });
  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);
  const [areFiltersVisible, toggleFilterState] = useState(true);
  
  const convertToTSV = (data) => {
    // Get headers from columns (including ID)
    const headers = data.columns.map((item) => item.colLabel).join("\t");

    // Get rows with values in the same order as columns
    const rows = data.rows
      .map((row) => {
        return data.columns
          .map((col) => {
            const value = row[col.dataKey];
            // Handle null/undefined values
            if (value === null || value === undefined) {
              return "-";
            }
            // Convert to string
            return String(value);
          })
          .join("\t");
      })
      .join("\n");

    return `${headers}\n${rows}`;
  };
  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };
  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch((err) => setIsCopy(true));
  };
  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  useEffect(() => {
    if (isPrintError) {
      const timer = setTimeout(() => {
        setIsPrintError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPrintError]);

  const handlePrint = () => {
    const success = printTableData({
      title: "Reservations Detail Report",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
      orientation: "landscape",
    });
    if (!success) {
      setIsPrintError(true);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header ">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Reservations Detail Report</h4>
            <Button
              label={areFiltersVisible ? "Hide Advanced Filter" : "Advanced Filter"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>
          <div className="buttonarea">
            <div className="create-btn-group">
              {/* {screenPermissions?.create && (
              <Button
                label={"Create Reservations Detail Report"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px", }}
                onClick={handleCreateReservationsDetailReport}
              />)} */}
            </div>
            <div className="copy-excel-print-group">
              <Button
                label={"Copy"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label={"Excel"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handleExport}
              />
              <Button
                label={"Print"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderComplianceRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderComplianceRecords() && !shouldRenderFullPageError();
  
  // Memoize filters to prevent unnecessary re-renders and ensure stable reference
  const filters = useMemo(() => {
    const getFilterValue = (key) => tableColumnFilters?.[key] ?? null;
    return {
      global: { value: null },
      ref: { value: getFilterValue("ref"), filterKey: "ref" },
      networkTitle: { value: getFilterValue("networkTitle"), filterKey: "networkTitle" },
      locationTitle: { value: getFilterValue("locationTitle"), filterKey: "locationTitle" },
      vehicle: { value: getFilterValue("vehicle"), filterKey: "vehicle" },
      bookingDate: { value: getFilterValue("bookingDate"), filterKey: "bookingDate" },
      pickup: { value: getFilterValue("pickup"), filterKey: "pickup" },
      dropoff: { value: getFilterValue("dropoff"), filterKey: "dropoff" },
      currencyCode: { value: getFilterValue("currencyCode"), filterKey: "currencyCode" },
      totalAmount: { value: getFilterValue("totalAmount"), filterKey: "totalAmount" },
      days: { value: getFilterValue("days"), filterKey: "days" },
      name: { value: getFilterValue("name"), filterKey: "name" },
      paymentStatus: { value: getFilterValue("paymentStatus"), filterKey: "paymentStatus" },
      rateType: { value: getFilterValue("rateType"), filterKey: "rateType" },
      status: { value: getFilterValue("status"), filterKey: "status" },
    };
  }, [tableColumnFilters]);

  const getToastProps = () => {
    let toastTitle = "";
    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle:
          "Unable to open print window. Please check your popup blocker settings.",
        shouldShowToast: true,
      };
    }
    if (isCopy) {
      let toastTitle = "Data copied to clipboard!";
      if (!isCopy) {
        toastTitle = "Error while Data copied to clipboard!";
      }

      return {
        severity: isCopy ? TOAST_SEVERITY.SUCCESS : TOAST_SEVERITY.ERROR,
        toastTitle,
        shouldShowToast: true,
      };
    }
  };
  const onSubmit = (data) => {
    const newFilters = { ...data };
    setDetailReportFilters(newFilters);
    setHasSearched(true);
    // Trigger API call with the new filters
    fetchReservationsDetailReports({
      pageNumber: 0,
      pageSize: paginationInfo?.pageSize || 10,
      sortField: "id",
      additinalFilter: newFilters,
    });
  };
  const handleResetFilters = () => {
    clearDetailReportFilters();
    if (clearTableColumnFilters) {
      clearTableColumnFilters();
    }
    setHasSearched(false);
    reset({
      startDate: "",
      endDate: "",
      type: "pickupDate",
      location: "",
      network: "",
      status: 0,
    });
  };
  const handleExport = () => {
    const exportPayload = {
      additinalFilter: detailReportFilters,
    };
    exportReservationsDetailReport(exportPayload);
  };
  
  // Callback to save table column filters when they change
  const handleTableFilterChange = (newFilters) => {
    // Extract just the filter values from the filters object
    const filterValues = {};
    Object.keys(newFilters).forEach(key => {
      if (key !== 'global' && newFilters[key]?.value !== null && newFilters[key]?.value !== undefined) {
        filterValues[key] = newFilters[key].value;
      }
    });
    if (setTableColumnFilters) {
      setTableColumnFilters(filterValues);
    }
  };
  
  const tableProps = {
    columns: tableData?.columns,
    paginationInfo,
    areFiltersVisible,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No Reservations Detail Report found.",
    filters,
    loadLazyData: fetchReservationsDetailReports,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || "id",
    initialSortOrder: savedTableState?.sortOrder,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    setDeleteConfirmationPopupVisibility,
    onEditRecord: editReservationsDetailReportRoute,
    onViewRecord: viewReservationRoute,
    additinalFilter: detailReportFilters,
    showViewButton: true,
    showEditButton: false,
    isSearch: hasSearched,
    onTableFilterChange: handleTableFilterChange,
    onTableStateChange,
  };
  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={ReservationsReservationsBreadcrumb} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}

          <div className="form-demo">
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledCalendarWithFloatingLabel
                    id="startDate"
                    name="startDate"
                    {...register("startDate")}
                    showTime={false}
                    maxDate={new Date(2090, 0, 1)}
                    dateFormat="yy/mm/dd"
                    control={control}
                    isError={errors["startDate"]}
                    errorMsg={getFormErrorMessage("startDate")}
                    label="Start Date"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledCalendarWithFloatingLabel
                    id="endDate"
                    name="endDate"
                    {...register("endDate")}
                    showTime={false}
                    maxDate={new Date(2090, 0, 1)}
                    dateFormat="yy/mm/dd"
                    control={control}
                    isError={errors["endDate"]}
                    errorMsg={getFormErrorMessage("endDate")}
                    label="End Date"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="type"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    optionLabel={"name"}
                    optionValue={"id"}
                    options={bookingTypeDD}
                    isError={errors["type"]}
                    errorMsg={getFormErrorMessage("type")}
                    label="Type"
                    labelClassName={"ml-2"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledMultiSelecttWithFloatingLabel
                    name="location"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel={"name"}
                    optionValue={"id"}
                    options={locationDD}
                    isError={errors["location"]}
                    errorMsg={getFormErrorMessage("location")}
                    label="Location"
                    labelClassName={"ml-2"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledMultiSelecttWithFloatingLabel
                    name="network"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel={"name"}
                    optionValue={"id"}
                    options={RateNetworkDD}
                    isError={errors["network"]}
                    errorMsg={getFormErrorMessage("network")}
                    label="Network"
                    labelClassName={"ml-2"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="status"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                    optionLabel={"name"}
                    optionValue={"id"}
                    options={listStatusDD}
                    isError={errors["status"]}
                    errorMsg={getFormErrorMessage("status")}
                    label="Status"
                    labelClassName={"ml-2"}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={3} mb={2}>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      margin: "0px 0px 0px 10px",
                    }}
                  >
                    <Button
                      type="button"
                      label={"Search"}
                      className="p-button-raised"
                      onClick={handleSubmit(onSubmit)}
                    />
                    <Button
                      type="button"
                      label={"Reset Filters"}
                      className="p-button-raised p-button-secondary"
                      onClick={handleResetFilters}
                    />
                  </div>
                </Grid>
              </Grid>
            </form>
          </div>
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderComplianceRecords() && (
              <Box id="printable">
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No Reservations Detail Report records to show"}
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
};
export default ReservationsDetailReport;
